<script>
import storageService from "@/services/storage.service";
import axios from "axios";
import AppIcon from "@/components/shared/AppIcon.vue";
import { parseNotificationResponse } from "@/helpers/notificationFormatter";

// Same routes as horizontal-menu-admin.js / horizontal-menu-staff.js. `match` lists the
// extra route prefixes that belong to an item so it stays highlighted on detail pages.
const ADMIN_NAV = [
  { label: "Dashboard", to: "/admin_dashboard", icon: "home", match: ["/admin_dashboard"] },
  {
    label: "All Staff",
    to: "/staff_users",
    icon: "users",
    match: ["/staff_users", "/single_staff", "/edit_staff", "/archived_staff_members"],
  },
  {
    label: "All Applicant",
    to: "/applicants",
    icon: "user-plus",
    match: [
      "/applicants",
      "/single_applicant",
      "/submit",
      "/admin_in_review",
      "/admin_verified",
      "/approved_application",
      "/rejected_application",
      "/incomplete_application",
    ],
  },
];

const STAFF_NAV = [
  { label: "Dashboard", to: "/staff_dashboard", icon: "home", match: ["/staff_dashboard"] },
  {
    label: "All Application",
    to: "/application_list",
    icon: "list",
    match: [
      "/application_list",
      "/approved_application",
      "/rejected_application",
      "/incomplete_application",
      "/view_application",
      "/review_application",
      "/pdf_data",
    ],
  },
  { label: "View Log", to: "/staff_view_log", icon: "file", match: ["/staff_view_log"] },
  {
    label: "Notifications",
    to: "/staff_notification",
    icon: "bell",
    match: ["/staff_notification"],
  },
  { label: "My Profile", to: "/my_profile", icon: "user", match: ["/my_profile"] },
];

const isStaffRole = (role) => role === "staff" || role === "verification officer";

export default {
  name: "PortalHeader",
  components: { AppIcon },
  data() {
    const role = storageService.getItem("userType");
    const staff = isStaffRole(role);
    return {
      navItems: staff ? STAFF_NAV : ADMIN_NAV,
      homeLink: staff ? "/staff_dashboard" : "/admin_dashboard",
      allNotifLink: staff ? "/staff_notification" : "/admin_notifications",
      menuOpen: false,
      panelOpen: false,
      notifCount: 0,
      notifs: [],
    };
  },
  computed: {
    // full-screen review pages (meta.nav === 1) only show the brand, bell and logout
    showNav() {
      return this.$route.meta.nav !== 1;
    },
    latest() {
      return this.notifs.slice(0, 5);
    },
    badge() {
      return this.notifCount > 99 ? "99+" : String(this.notifCount);
    },
  },
  watch: {
    "$route.path"() {
      this.menuOpen = false;
      this.panelOpen = false;
    },
  },
  mounted() {
    this.fetchNotifications();
    document.addEventListener("click", this.onDocumentClick);
    document.addEventListener("keydown", this.onKeydown);
    if (this.$emitter) this.$emitter.on("notifications:read", this.onNotificationsRead);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    if (this.$emitter) this.$emitter.off("notifications:read", this.onNotificationsRead);
  },
  methods: {
    isActive(item) {
      const path = this.$route.path;
      return item.match.some((p) => path === p || path.startsWith(`${p}/`));
    },
    fetchNotifications() {
      axios
        .post("/applications/kick_notif/", {
          token: storageService.getItem("accessToken"),
        })
        .then((response) => {
          const { items, count } = parseNotificationResponse(response.data);
          this.notifs = items;
          this.notifCount = count;
        })
        .catch((error) => console.error("Could not load notifications:", error));
    },
    markAsRead() {
      if (!this.notifCount) return;
      axios
        .post("/applications/kick_notif/", {
          token: storageService.getItem("accessToken"),
          read: true,
        })
        .then((response) => {
          // keep the list as is so unread items stay highlighted while the panel is open
          this.notifCount = parseNotificationResponse(response.data).count;
        })
        .catch((error) => console.error("Could not mark notifications as read:", error));
    },
    togglePanel() {
      this.panelOpen = !this.panelOpen;
      if (this.panelOpen) {
        this.menuOpen = false;
        this.markAsRead();
      }
    },
    // clicking a notification closes the panel and opens the notifications page
    openNotification() {
      this.panelOpen = false;
      this.$router.push(this.allNotifLink);
    },
    onNotificationsRead() {
      this.notifCount = 0;
    },
    onDocumentClick(event) {
      const wrap = this.$refs.bellWrap;
      if (this.panelOpen && wrap && !wrap.contains(event.target)) {
        this.panelOpen = false;
      }
      const nav = this.$refs.navWrap;
      if (this.menuOpen && nav && !nav.contains(event.target)) {
        this.menuOpen = false;
      }
    },
    onKeydown(event) {
      if (event.key === "Escape") {
        this.panelOpen = false;
        this.menuOpen = false;
      }
    },
  },
};
</script>

<template>
  <header class="ph">
    <div class="ph__inner">
      <router-link :to="homeLink" class="ph__brand" aria-label="Eastern Credit Union home">
        <img src="@/assets/images/ECU/ECU-logo.jpg" alt="Eastern Credit Union" />
      </router-link>
      <span v-if="showNav" class="ph__divider ph__divider--brand" aria-hidden="true"></span>

      <div v-if="showNav" ref="navWrap" class="ph__navwrap">
        <button
          type="button"
          class="ph__burger"
          :aria-expanded="menuOpen ? 'true' : 'false'"
          aria-controls="ph-nav"
          aria-label="Toggle navigation"
          @click="menuOpen = !menuOpen"
        >
          <AppIcon :name="menuOpen ? 'x' : 'menu'" :size="18" />
        </button>

        <nav id="ph-nav" class="ph__nav" :class="{ 'is-open': menuOpen }" aria-label="Main">
          <router-link
            v-for="item in navItems"
            :key="item.to"
            :to="item.to"
            class="ph__link"
            :class="{ 'is-active': isActive(item) }"
            :aria-current="isActive(item) ? 'page' : null"
          >
            <AppIcon :name="item.icon" :size="16" />
            <span>{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <div class="ph__actions">
        <div ref="bellWrap" class="ph__bellwrap">
          <button
            type="button"
            class="ph__bell"
            :aria-expanded="panelOpen ? 'true' : 'false'"
            aria-haspopup="true"
            :aria-label="notifCount ? `Notifications, ${notifCount} unread` : 'Notifications'"
            @click="togglePanel"
          >
            <AppIcon name="bell" :size="21" :stroke="1.7" />
            <span v-if="notifCount > 0" class="ph__badge">{{ badge }}</span>
          </button>

          <transition name="ph-pop">
            <div v-if="panelOpen" class="ph__panel" role="dialog" aria-label="Recent notifications">
              <div class="ph__panel-head">
                <h6>Notifications</h6>
                <router-link :to="allNotifLink">View all</router-link>
              </div>
              <ul v-if="latest.length" class="ph__panel-list">
                <li
                  v-for="(n, idx) in latest"
                  :key="idx"
                  :class="{ 'is-unread': n.unread }"
                  role="link"
                  tabindex="0"
                  @click="openNotification"
                  @keydown.enter.prevent="openNotification"
                >
                  <strong :title="n.title">{{ n.title }}</strong>
                  <p v-if="n.message" :title="n.message">{{ n.message }}</p>
                  <small v-if="n.date"><AppIcon name="clock" :size="10" /> {{ n.date }}</small>
                </li>
              </ul>
              <div v-else class="ph__panel-empty">
                <AppIcon name="bell" :size="21" />
                <span>You're all caught up</span>
              </div>
            </div>
          </transition>
        </div>

        <span class="ph__divider" aria-hidden="true"></span>

        <router-link to="/logout?logout" class="ph__logout">
          <AppIcon name="logout" :size="16" />
          <span>Logout</span>
        </router-link>
      </div>
    </div>
  </header>
</template>

<style scoped>
.ph {
  position: sticky;
  top: 0;
  z-index: 100;
  background: rgba(255, 255, 255, 0.86);
  -webkit-backdrop-filter: saturate(160%) blur(11.2px);
  backdrop-filter: saturate(160%) blur(11.2px);
  box-shadow: 0 4.8px 22.4px rgba(20, 40, 90, 0.06);
}
.ph__inner {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 19.2px;
  height: 80px;
  display: flex;
  align-items: center;
  gap: 14.4px;
}
.ph__brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.ph__brand img[data-v-d4bcddc8] {
    height: 65.2px;
    width: auto;
    display: block;
    aspect-ratio: 250 / 109;
    -o-object-fit: contain;
    object-fit: contain;
}
.ph__divider {
  width: 1px;
  height: 40px;
  background: linear-gradient(180deg, transparent, #d9dce8, transparent);
  flex-shrink: 0;
}

/* ---------- nav ---------- */
.ph__navwrap {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}
.ph__nav {
  display: flex;
  align-items: center;
  gap: 4.8px;
}
.ph__link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8.8px 16px;
  border-radius: 9.6px;
  font-size: var(--ecu-fs-body);
  font-weight: 600;
  color: var(--ecu-navy, #0b1f4b);
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease,
    transform 0.2s ease;
}
.ph__link:hover {
  background: #fff1e6;
  color: var(--ecu-orange-dark, #e84d00);
}
.ph__link.is-active {
  color: #fff;
  background: linear-gradient(135deg, #ff8a2a 0%, #f2610f 100%);
  box-shadow: 0 6.4px 16px rgba(242, 97, 15, 0.28);
}
.ph__burger {
  display: none;
  width: 35.2px;
  height: 35.2px;
  border: 0;
  border-radius: 9.6px;
  background: #fff1e6;
  color: var(--ecu-orange-dark, #e84d00);
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

/* ---------- right side ---------- */
.ph__actions {
  display: flex;
  align-items: center;
  gap: 14.4px;
  margin-left: auto;
}
.ph__bellwrap {
  position: relative;
}
.ph__bell {
  position: relative;
  width: 36.8px;
  height: 36.8px;
  border: 0;
  border-radius: 11.2px;
  background: transparent;
  color: var(--ecu-navy, #0b1f4b);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease;
}
.ph__bell:hover,
.ph__bell[aria-expanded="true"] {
  background: #fff1e6;
}
.ph__badge {
  position: absolute;
  top: 2px;
  right: 1px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 799.2px;
  background: #ef1c1c;
  color: #fff;
  font-size: 8.8px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  box-shadow: 0 0 0 2px #fff;
}
.ph__logout[data-v-d4bcddc8] {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8.8px 17.6px;
    border-radius: 9.6px;
    background: #fff;
    color: #ff0000;
    border: 1px solid red;
    font-size: var(--ecu-fs-body);
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.ph__logout[data-v-d4bcddc8][data-v-d4bcddc8][data-v-d4bcddc8]:hover {
    color: #ffffff;
    border: 1px solid red;
    transform: translateY(-1px);
    box-shadow: 0 9.6px 20.8px #ff232385;
    background: #ff0e0e;
}

/* ---------- notification panel ---------- */
.ph__panel {
  position: absolute;
  right: 0;
  top: calc(100% + 9.6px);
  width: 288px;
  max-width: calc(100vw - 19.2px);
  background: #fff;
  border-radius: 14.4px;
  border: 1px solid #eef0f6;
  box-shadow: 0 19.2px 48px rgba(20, 40, 90, 0.18);
  overflow: hidden;
  transform-origin: top right;
}
.ph__panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12.8px 14.4px;
  border-bottom: 1px solid #f0f1f6;
}
.ph__panel-head h6 {
  margin: 0;
  font-size: var(--ecu-fs-body);
  font-weight: 700;
  color: var(--ecu-navy, #0b1f4b);
}
.ph__panel-head a {
  font-size: var(--ecu-fs-small);
  font-weight: 600;
  color: var(--ecu-orange-dark, #e84d00);
  text-decoration: none;
}
.ph__panel-list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 256px;
  overflow-y: auto;
}
.ph__panel-list li {
  padding: 11.2px 14.4px;
  border-bottom: 1px solid #f4f5fa;
  position: relative;
}
.ph__panel-list li {
  cursor: pointer;
}
.ph__panel-list li:focus-visible {
  outline: 2px solid var(--ecu-orange, #f97316);
  outline-offset: -2px;
}
.ph__panel-list li:last-child {
  border-bottom: 0;
}
.ph__panel-list li.is-unread {
  background: #fffaf5;
}
.ph__panel-list li.is-unread::before {
  content: "";
  position: absolute;
  left: 5.6px;
  top: 17.6px;
  width: 4.8px;
  height: 4.8px;
  border-radius: 50%;
  background: var(--ecu-orange, #f97316);
}
.ph__panel-list strong,
.ph__panel-list p {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.ph__panel-list strong {
  display: -webkit-box;
  font-size: var(--ecu-fs-body);
  color: var(--ecu-navy, #0b1f4b);
}
.ph__panel-list li:not(.is-unread) strong {
  font-weight: 500;
}
.ph__panel-list p {
  margin: 2.4px 0 3.2px;
  font-size: var(--ecu-fs-small);
  color: #5f6579;
}
.ph__panel-list small {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--ecu-fs-small);
  color: #8b90a3;
}
.ph__panel-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6.4px;
  padding: 25.6px 14.4px;
  color: #8b90a3;
  font-size: var(--ecu-fs-body);
}
.ph-pop-enter-active,
.ph-pop-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}
.ph-pop-enter-from,
.ph-pop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

/* ---------- responsive ---------- */
@media (max-width: 991px) {
  .ph__inner {
    height: 54.4px;
    padding: 0 12.8px;
    gap: 9.6px;
  }
  .ph__brand img {
    height: 36.8px;
  }
  .ph__divider--brand {
    display: none;
  }
  .ph__navwrap {
    flex: 0 0 auto;
  }
  .ph__burger {
    display: inline-flex;
  }
  .ph__nav {
    display: none;
    position: absolute;
    left: 9.6px;
    right: 9.6px;
    top: calc(100% + 6.4px);
    flex-direction: column;
    align-items: stretch;
    gap: 3.2px;
    padding: 8px;
    background: #fff;
    border-radius: 14.4px;
    box-shadow: 0 19.2px 48px rgba(20, 40, 90, 0.18);
  }
  .ph__nav.is-open {
    display: flex;
  }
  .ph__actions {
    gap: 6.4px;
  }
  .ph__actions .ph__divider {
    display: none;
  }
}
@media (max-width: 575px) {
  .ph__logout {
    padding: 8.8px 10.4px;
  }
  .ph__logout span {
    display: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .ph__link,
  .ph__logout,
  .ph-pop-enter-active,
  .ph-pop-leave-active {
    transition: none;
  }
}
</style>